import { useState, useEffect } from "react"
import { Store } from 'lucide-react'
import ChangePassword from "../../../cors/ChangePassword"
import Cookies from 'js-cookie'
import { toast } from "sonner"
import BankSelect from "../../../pages/auth/components/BankSelect";
import SallerBank from "../../../cors/SallerBank";
import { BASE_URL } from '../../../apiConfig';
import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";

const LEVEL_OPTIONS = [
  { value: "Beginner", label: "Beginner" },
  { value: "Intermediate", label: "Intermediate" },
  { value: "Advanced", label: "Advanced" }
]

export default function ProfileSeller({ user }) {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    name_store: "",
    nameBank: "",
    rib: "",
    address: "",
    phone: "",
    email: "",
    level: "Beginner"
  })
  const [fieldErrors, setFieldErrors] = useState({})
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [removeLogoFlag, setRemoveLogoFlag] = useState(false) // Add this state

  // Initialize form data when userData changes
  useEffect(() => {
    if (user && user.saller_info_active) {
      setFormData({
        name: user.saller_info_active.name || "",
        name_store: user.saller_info_active.name_store || "",
        nameBank: user.saller_info_active.name_bank || "",
        rib: user.saller_info_active.rib || "",
        address: user.saller_info_active.address || "",
        phone: user.saller_info_active.phone || "",
        email: user.email || "",
        level: user.saller_info_active.level || "Beginner"
      })
      
      // Set logo preview if available
      if (user.saller_info_active.logo) {
        setLogoPreview(`${BASE_URL}/storage/${user.saller_info_active.logo}`)
      }
    }
  }, [user])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleBankChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(t("profile.seller.image_type_error"))
        return
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("profile.seller.image_size_error"))
        return
      }
      
      setLogoFile(file)
      setRemoveLogoFlag(false) // Reset remove flag when a new file is selected
      
      // Create preview
      const reader = new FileReader()
      reader.onload = () => {
        setLogoPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeLogo = () => {
    setLogoFile(null)
    setLogoPreview(null)
    setRemoveLogoFlag(true) // Set the flag to remove the logo
  }

  const validateForm = () => {
    const errors = {}

    console.log(formData);
    
    
    // Name validation
    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = [t("profile.seller.name_error")]
    }
    
    // Store name validation
    if (!formData.name_store || formData.name_store.trim().length < 2) {
      errors.name_store = [t("profile.seller.store_name_error")]
    }
    
    if (!formData.nameBank || formData.nameBank.trim().length < 2) {
      errors.nameBank  = [t("profile.seller.bank_error")]
    }
    
    // RIB validation
    if (!formData.rib || formData.rib.length < 10) {
      errors.rib = [t("profile.seller.rib_error")]
    }
    
    // Address validation
    if (!formData.address || formData.address.trim().length < 5) {
      errors.address = [t("profile.seller.address_error")]
    }
    
    // Phone validation
    const phoneRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      errors.phone = [t("profile.seller.phone_error")]
    }
    
    // Email validation
    const emailRegex = /^[^\s.]+.[^\s.]+\.[^\s.]+$/
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = [t("profile.seller.email_error")]
    }
    
    return errors
  }

  const handleSubmit = async () => {
    const clientErrors = validateForm();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      toast.error(t("profile.seller.validation_error"));
      return;
    }

    setIsLoading(true);
    setFieldErrors({});

    try {

      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("name_store", formData.name_store);
      formDataToSend.append("nameBank", formData.nameBank);
      formDataToSend.append("rib", formData.rib);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("level", formData.level);

      // Add remove_logo flag if logo was removed
      if (removeLogoFlag) {
        formDataToSend.append("remove_logo", 1);
      }

      if (logoFile) {
        formDataToSend.append("logo", logoFile);
      }

      // Laravel trick: allow POST with _method=PUT
      formDataToSend.append("_method", "PUT");

      const response = await fetch(`${BASE_URL}/api/update-saller-profile/${user.id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle server validation errors
        if (result.errors) {
          setFieldErrors(result.errors);
          toast.error(t("profile.seller.server_error"));
          return;
        }
        throw new Error(result.message || t("profile.seller.update_error"));
      }

      toast.success(t("profile.seller.update_success"));
      Cookies.set("user", JSON.stringify(result.user), { expires: 7 });
      setIsEditing(false);
      setRemoveLogoFlag(false); // Reset the flag after successful update

    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || t("profile.seller.update_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (user && user.saller_info_active) {
      setFormData({
        name: user.saller_info_active.name || "",
        name_store: user.saller_info_active.name_store || "",
        nameBank: user.saller_info_active.name_bank || "",
        rib: user.saller_info_active.rib || "",
        address: user.saller_info_active.address || "",
        phone: user.saller_info_active.phone || "",
        email: user.email || "",
        level: user.saller_info_active.level || "Beginner"
      })
      
      // Reset logo preview
      if (user.saller_info_active.logo) {
        setLogoPreview(`${BASE_URL}/storage/${user.saller_info_active.logo}`)
      } else {
        setLogoPreview(null)
      }
    }
    setLogoFile(null)
    setIsEditing(false)
    setFieldErrors({})
    setRemoveLogoFlag(false) // Reset the flag on cancel
  }

  if (!user || !user.saller_info_active) {
    return <div className="max-w-6xl mx-auto p-6">{t('profile.seller.loading')}</div>
  }

  const sellerInfo = user.saller_info_active

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 md:w-32 md:h-32 bg-white/50 rounded-full border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                {logoPreview ? (                  
                  <img 
                    src={
                      logoPreview?.startsWith("data:")
                        ? logoPreview
                        : logoPreview
                          ? logoPreview
                          : "/default-logo.png"
                    }
                    alt="Store Logo" 
                    width={64} 
                    height={64} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Store className="h-10 w-10 md:w-16 md:h-16 text-white" />
                )}
              </div>
              <div className="text-white">
                <h1 className="text-xl md:text-2xl font-bold">{formData.name}</h1>
                <p className="text-xs text-emerald-100 mb-1">{formData.email}</p>
                <p className="text-emerald-100 text-xs">{t('profile.seller.level_prefix')} {formData.level === "Beginner" ? 1 : formData.level === "Intermediate" ? 2 : 3},0</p>
              </div>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold ">{formData.level === "Beginner" ? 1 : formData.level === "Intermediate" ? 2 : 3},0 ★</div>
                <div className="text-xs text-emerald-500">{t('profile.seller.level_badge')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="p-8 max-xl:px-5">
          <div className="space-y-6"> 
            <div>
              {!isEditing ? (
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-green-100 text-[var(--color-primary-500)] cursor-pointer px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span className="text-sm max-xl:text-xs">{t('profile.seller.edit_profile')}</span>
                  </button>
                </div>
              ) : (
                <div className="flex justify-end gap-2">
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className={`bg-[var(--color-primary-500)] text-white cursor-pointer px-6 py-2 rounded-lg font-semibold hover:bg-[var(--color-primary-400)] transition-colors duration-200 flex items-center gap-2 ${
                      isLoading ? 'opacity-70' : ''
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="max-xl:text-xs">{t('profile.seller.saving')}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm max-xl:text-xs">{t('profile.seller.save')}</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="bg-red-100 text-red-600 cursor-pointer px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm max-xl:text-xs">{t('profile.seller.cancel')}</span>
                  </button>
                </div>
              )}
            </div>
            <div className="flex max-xl:px-3">
              <div className="flex items-center mb-4 gap-3">
                <div className="bg-[var(--color-primary-50)] p-3 rounded-lg">
                  <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">{t('profile.seller.personal_info')}</h2>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-xl:px-3">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center">
                    {t('profile.seller.full_name')}
                  </label>
                  {isEditing ? 
                    <>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.name && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.name[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.name || t('profile.seller.not_specified')}
                    </p>
                  }
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center">
                    {t('profile.seller.store_name')}
                  </label>
                  {isEditing ? 
                    <>
                      <input
                        type="text"
                        name="name_store"
                        value={formData.name_store}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.name_store ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.name_store && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.name_store[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.name_store || t('profile.seller.not_specified')}
                    </p>
                  }
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center">
                    {t('profile.seller.bank_name')}
                  </label>
                  {isEditing ? 
                    <>
                      <BankSelect
                        type={false}
                        formData={formData}
                        handleChange={handleBankChange}
                        errors={fieldErrors}
                      />
                      {fieldErrors.nameBank && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.nameBank[0]}</p>
                      )}
                    </>
                    :
                    <div className="text-gray-700 text-[10px] bg-gray-50 px-4 py-3 rounded-2xl">
                      <SallerBank bank={formData.nameBank} />  
                    </div>
                  }
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">{t('profile.seller.rib')}</label>
                  {isEditing ? 
                    <>
                      <input
                        type="text"
                        name="rib"
                        value={formData.rib}
                        onChange={handleInputChange}
                        className={`w-full font-mono py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.rib ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.rib && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.rib[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl font-mono">
                      {formData.rib || t('profile.seller.not_specified')}
                    </p>
                  }
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('profile.seller.logo')}</label>
                  
                  {isEditing ? (
                    <div className="relative border border-gray-200 rounded-xl bg-gray-50 focus-within:ring-2 focus-within:ring-[var(--color-primary-200)]">
                      <input
                        type="file"
                        id="logo"
                        name="logo"
                        onChange={handleLogoChange}
                        className="w-full cursor-pointer text-xs pr-11 pl-4 py-3 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[var(--color-primary-50)] file:text-[var(--color-primary-500)] hover:file:bg-[var(--color-primary-100)]"
                        accept="image/*"
                      />
                      <svg
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>

                      {logoPreview ? (
                        <div className="mt-4 flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-gray-50">
                          <div className="flex items-center gap-3">
                            <img
                              src={
                                logoPreview?.startsWith("data:")
                                  ? logoPreview
                                  : logoPreview
                                    ? logoPreview
                                    : "/default-logo.png"
                              }
                              alt={t('profile.seller.current_logo')}
                              className="h-16 w-16 object-contain rounded-lg"
                            />
                            <span className="text-sm text-gray-700">{logoFile?.name || t('profile.seller.current_logo')}</span>
                          </div>
                          <button
                            type="button"
                            onClick={removeLogo}
                            className="text-red-500 cursor-pointer hover:text-red-700 p-1 rounded-full hover:bg-red-100 transition-colors"
                            aria-label={t('profile.seller.delete_logo_aria')}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </button>
                        </div>
                      ) : (
<p className="text-gray-500 text-xs italic mt-4 p-3">{t('profile.seller.no_logo')}</p>
                      )}
                    </div>
                  ) : (
                    // View mode
                    sellerInfo.logo && !removeLogoFlag ? (
                      <div className="mt-4 flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-gray-50">
                        <div className="flex items-center gap-3">
                          <img
                            src={
                              logoPreview?.startsWith("data:")
                                ? logoPreview
                                : logoPreview
                                  ? logoPreview
                                  : "/default-logo.png"
                            }
                            alt={t('profile.seller.current_logo')}
                            className="h-16 w-16 object-contain rounded-lg"
                          />
                          <span className="text-sm text-gray-700">{t('profile.seller.current_logo')}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-xs italic mt-4 p-3">Aucun logo téléchargé</p>
                    )
                  )}
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center">
                    {t('profile.seller.email')}
                  </label>
                  {isEditing ? 
                    <>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.email && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.email[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.email || t('profile.seller.not_specified')}
                    </p>
                  }
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center">
                    {t('profile.seller.phone')}
                  </label>
                  {isEditing ? 
                    <>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.phone && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.phone[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.phone || t('profile.seller.not_specified')}
                    </p>
                  }
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center">
                    {t('profile.seller.address')}
                  </label>
                  {isEditing ? 
                    <>
                      <input
                      type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        rows={3}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.address ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.address && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.address[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.address || t('profile.seller.not_specified')}
                    </p>
                  }
                </div>
                  <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">{t('profile.seller.seller_level')}</label>
                  {isEditing ? 
                    <>
                      <select
                        name="level"
                        value={formData.level}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.level ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      >
                        {LEVEL_OPTIONS.map(level => (
                          <option key={level.value} value={level.value}>
                            {t(`profile.seller.level_${level.value.toLowerCase()}`)}
                          </option>
                        ))}
                      </select>
                      {fieldErrors.level && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.level[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.level || t('profile.seller.not_specified')}
                    </p>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <ChangePassword userId={user.id} />
    </div>
  )
}


